import { createContext, useContext, type ReactNode } from 'react';

export interface User {
  uid: string;
  email: string;
  displayName: string | null;
  propertyIds: string[];
  preferredProperty: string | null;
  role: string;
  /**
   * Effective permissions from the server (role base + grants - denials).
   * Never re-derive these from `role` on the client: a second copy of the
   * role -> permission map drifts, and drifting open is a silent access grant.
   */
  permissions: string[];
  organizationId: string | null;
  organizationRole: string | null;
  tenantId: string | null;
  tenantRole: string | null;
  tenantPropertyIds: string[];
  tenantPortfolioAccess: boolean;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: AuthContextType;
}) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
